import { useEffect, useRef, useState } from 'react';
import { TYPING_HOLD_MS, TYPING_INTERVAL_MS } from './constants';

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useTypingPlaceholder = ({
    texts,
    startIndex = 0,
    instant = false,
    disabled = false,
    intervalMs = TYPING_INTERVAL_MS,
    holdMs = TYPING_HOLD_MS,
}) => {
    const [typed, setTyped] = useState(null);
    const timeoutRef = useRef(null);
    const isFocusedRef = useRef(false);
    const isHoveredRef = useRef(false);

    const clearTimer = () => {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
    };

    const startTyping = () => {
        if (disabled || typed !== null || !texts.length) {
            return;
        }

        if (instant || prefersReducedMotion()) {
            setTyped({ index: startIndex, length: texts[startIndex].length });
            return;
        }

        const type = (index, length) => {
            setTyped({ index, length });

            if (length < texts[index].length) {
                timeoutRef.current = setTimeout(type, intervalMs, index, length + 1);
            } else if (texts.length > 1) {
                timeoutRef.current = setTimeout(erase, holdMs, index, texts[index].length - 1);
            }
        };

        const erase = (index, length) => {
            if (length === 0) {
                type((index + 1) % texts.length, 0);
                return;
            }

            setTyped({ index, length });
            timeoutRef.current = setTimeout(erase, intervalMs / 3, index, length - 1);
        };

        type(startIndex, 0);
    };

    const reset = () => {
        clearTimer();
        setTyped(null);
    };

    const handleMouseEnter = () => {
        isHoveredRef.current = true;
        startTyping();
    };

    const handleMouseLeave = () => {
        isHoveredRef.current = false;

        if (!isFocusedRef.current) {
            reset();
        }
    };

    const handleFocus = () => {
        isFocusedRef.current = true;
        startTyping();
    };

    const handleBlur = () => {
        isFocusedRef.current = false;

        if (!isHoveredRef.current) {
            reset();
        }
    };

    const handleInput = () => {
        clearTimer();

        if (!disabled && texts.length) {
            setTyped((prev) => {
                const index = prev?.index ?? startIndex;

                return { index, length: texts[index].length };
            });
        }
    };

    useEffect(() => clearTimer, []);

    return {
        typedText: disabled || typed === null ? null : texts[typed.index].slice(0, typed.length),
        handleMouseEnter,
        handleMouseLeave,
        handleFocus,
        handleBlur,
        handleInput,
    };
};
